import { useEffect, useMemo, useState } from 'react'
import {
  Building2, CheckCircle2, Pencil, Plus, QrCode, Scale, Sparkles, TriangleAlert, Undo2, X,
} from 'lucide-react'
import {
  Bidi, Card, CardHeader, EmptyState, ErrorState, LoadingState, NumberField, useLocale,
} from '@hk/ui'
import {
  addBuilding, addFloor, fetchRoomAdmin, formatCount, formatCredits, nameIn, pluralise, saveRoom,
  saveRoomType, setCleaningRules,
  setMinutesPerCredit, templateName,
  setRoomInHotel, translateError,
  type AdminBuilding,
  type AdminChecklistTemplate, type AdminFloor, type AdminRoomType, type DeepCleanFrequency,
  type RoomAdminBoard, type RoomAdminRow,
} from '@hk/core'
import { STRINGS } from '../i18n/strings'

/* ============================================================================
   W-127, THE OTHER HALF — ONE ROOM AT A TIME.

   The spreadsheet puts five hundred rooms in on a Thursday. This is every day after
   that, and it exists because of what the spreadsheet REFUSES to do: a load never takes
   a room out of the hotel and never puts one back, because a partial export is
   indistinguishable from an instruction to close four hundred rooms. Without somewhere
   else to do it, the first hotel that knocks a room through would edit a five-hundred-row
   file to change one cell — and would learn to do that every time.

   THE TWO WORDS THIS SCREEN KEEPS APART, and it is the main thing it does:

     OUT OF SERVICE   the room exists, is shut for repair, is on the room board, and a
                      supervisor sets it. Not here.
     OUT OF THE HOTEL the room is not one of the hotel's rooms any more. Only here, only
                      an administrator, and always with a reason.

   THE ROOMS THAT ARE OUT OF THE HOTEL ARE HIDDEN BY DEFAULT AND FINDABLE. Hidden,
   because a hotel of five hundred rooms does not want to scroll past the store cupboard
   it made in June; findable, because the commonest refusal this whole feature gives is
   "306 exists but is out of the hotel", and a room you cannot see is a room you cannot
   put back.
   ========================================================================= */

type Editing =
  | { kind: 'none' }
  | { kind: 'room'; room: RoomAdminRow | null }
  | { kind: 'inHotel'; room: RoomAdminRow; to: boolean }
  /** W-127's other half. A LIST of rooms, because one room and "every Twin that is
      missing something" are the same write — see `setCleaningRules`. */
  | { kind: 'cleaning'; rooms: RoomAdminRow[] }
  | { kind: 'floor' }
  | { kind: 'building' }
  /** **A KIND OF ROOM** (Sprint 49). Edit only — a type is created by the hotel's own room
      sheet, because one invented here under a code the next upload does not use would
      split four hundred rooms across two types that both look perfectly valid. */
  | { kind: 'roomType'; type: AdminRoomType }
  /** WHAT ONE CREDIT IS WORTH, for the whole property. Its own form because it is its own
      act: the others change one room or a set of rooms, and this re-prices the building. */
  | { kind: 'divisor' }

export function RoomEditor({ propertyId }: { propertyId: string }) {
  const { locale } = useLocale()
  const t = STRINGS[locale]

  const [board, setBoard] = useState<RoomAdminBoard | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [reload, setReload] = useState(0)
  const [search, setSearch] = useState('')
  const [showRemoved, setShowRemoved] = useState(false)
  const [editing, setEditing] = useState<Editing>({ kind: 'none' })
  const [said, setSaid] = useState<{ text: string; warn?: string } | null>(null)

  useEffect(() => {
    let live = true
    setError(null)
    fetchRoomAdmin(propertyId)
      .then((next) => { if (live) setBoard(next) })
      .catch((caught: unknown) => { if (live) setError(caught) })
    return () => { live = false }
  }, [propertyId, reload])

  const shown = useMemo(() => {
    if (!board) return []
    /* MATCHED ON THE ROOM NUMBER AND ON THE TYPE, and the number is matched as text
       rather than as a prefix: somebody looking for 305 types 305, and somebody looking
       at a floor types 3 and expects the whole floor. */
    const needle = search.trim().toLowerCase()
    return board.rooms.filter((room) =>
      (showRemoved || room.inTheHotel)
      && (needle === ''
        || room.roomNumber.toLowerCase().includes(needle)
        || room.floor.toLowerCase().includes(needle)
        || room.typeCode.toLowerCase().includes(needle)
        || room.typeName.toLowerCase().includes(needle)
        || (room.typeNameAr ?? '').includes(needle)))
  }, [board, search, showRemoved])

  /* ROOMS NOBODY CAN BE GIVEN. Counted over the WHOLE hotel rather than over the filtered
     list: the number is about what is blocking work, and a search box is not a reason for
     it to change. A room out of the hotel is not counted — it has no work to block. */
  const unconfigured = useMemo(
    () => (board?.rooms ?? []).filter(
      (r) => r.inTheHotel && r.missingCleaningRules.length > 0),
    [board])

  const removedCount = board?.rooms.filter((r) => !r.inTheHotel).length ?? 0

  function done(text: string, warn?: string) {
    setSaid(warn === undefined ? { text } : { text, warn })
    setEditing({ kind: 'none' })
    setReload((n) => n + 1)
  }

  if (error !== null && board === null) {
    return (
      <ErrorState
        body={translateError(error, locale).message}
        onRetry={() => setReload((n) => n + 1)}
      />
    )
  }
  if (!board) return <LoadingState />

  return (
    <div className="flex flex-col gap-3.5">
      <Card>
        <CardHeader title={t.editTitle} icon={<Building2 className="size-4" />} />
        <div className="flex flex-col gap-3 p-3.5">
          <p className="max-w-[68ch] text-[12.5px] leading-relaxed text-muted-foreground">
            {t.editIntro}
          </p>

          {/* ── WHAT IS STOPPING WORK, AT THE TOP, WITH THE WAY OUT NEXT TO IT ──────

              Rooms with no cleaning rules are not a tidiness problem: they are rooms no
              supervisor can give to anybody, and nothing else on any other screen says
              so. The pack puts the sentence on the row; it belongs at the top as well,
              because a hotel that has just loaded five hundred rooms has five hundred
              amber rows and no reason to scroll.

              AND THE BUTTON IS BESIDE THE SENTENCE. A rule with teeth needs the way out
              built at the same time — the same lesson as the count hold and the
              photograph queue. */}
          {unconfigured.length > 0 && (
            <div
              role="status"
              className="flex flex-wrap items-center gap-2.5 rounded-hk border
                         border-[#fcd34d] bg-[#fffbeb] p-2.5 text-[12.5px]
                         text-[#92400e] dark:border-[#b45309] dark:bg-[#78350f]/25
                         dark:text-[#fcd34d]"
            >
              <TriangleAlert className="size-4 shrink-0" />
              <Bidi>
                {pluralise(
                  t.cleanUnconfigured, unconfigured.length, locale,
                  formatCount(unconfigured.length, locale))}
              </Bidi>
              <button
                type="button"
                onClick={() => setEditing({ kind: 'cleaning', rooms: unconfigured })}
                className="ms-auto inline-flex items-center gap-1.5 rounded-hk border
                           border-current px-2.5 py-1 text-xs font-medium cursor-pointer"
              >
                <Sparkles className="size-3.5" />
                {t.cleanFixThem}
              </button>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2.5">
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t.editSearch}
              aria-label={t.editSearch}
              className="h-8 w-48 rounded-hk border border-input bg-background px-2.5 text-[12.5px]"
            />
            <button
              type="button"
              onClick={() => setEditing({ kind: 'room', room: null })}
              className="inline-flex items-center gap-1.5 rounded-hk bg-[var(--hk-primary)]
                         px-3 py-1.5 text-xs font-medium text-white cursor-pointer"
            >
              <Plus className="size-3.5" />
              {t.editAdd}
            </button>
            <button
              type="button"
              onClick={() => setEditing({ kind: 'building' })}
              className="inline-flex items-center gap-1.5 rounded-hk border border-border
                         px-3 py-1.5 text-xs font-medium hover:bg-muted cursor-pointer"
            >
              <Plus className="size-3.5" />
              {t.editAddBuilding}
            </button>
            <button
              type="button"
              onClick={() => setEditing({ kind: 'floor' })}
              className="inline-flex items-center gap-1.5 rounded-hk border border-border
                         px-3 py-1.5 text-xs font-medium hover:bg-muted cursor-pointer"
            >
              <Plus className="size-3.5" />
              {t.editAddFloor}
            </button>

            {/* WHAT A CREDIT IS WORTH HERE, READABLE BEFORE IT IS EDITABLE.

                It is a BUTTON SHOWING THE CURRENT VALUE rather than a field, and that is
                the point of it: the number is read a hundred times for every time it is
                changed. A supervisor looking at "45 min · 2.25" in the table needs to
                know the divisor to make sense of the second figure, and almost never
                needs to change it — so the screen answers the common question in the
                toolbar and puts the rare act one press away. */}
            <button
              type="button"
              onClick={() => setEditing({ kind: 'divisor' })}
              className="inline-flex items-center gap-1.5 rounded-hk border border-border
                         px-2.5 py-1.5 text-xs text-muted-foreground hover:bg-muted
                         cursor-pointer"
            >
              <Scale className="size-3.5" />
              <Bidi>
                {t.divisorCurrent.replace(
                  '{minutes}', formatCount(board.minutesPerCredit, locale))}
              </Bidi>
            </button>

            {/* OFFERED ONLY WHEN THERE IS SOMETHING BEHIND IT. A checkbox that reveals
                nothing is a checkbox somebody ticks twice and then distrusts. */}
            {removedCount > 0 && (
              <label className="ms-auto inline-flex items-center gap-1.5 text-[12.5px] text-muted-foreground">
                <input
                  type="checkbox"
                  checked={showRemoved}
                  onChange={(e) => setShowRemoved(e.target.checked)}
                  className="size-3.5 cursor-pointer"
                />
                {t.editShowRemoved} ({formatCount(removedCount, locale)})
              </label>
            )}
          </div>
        </div>
      </Card>

      {/* ══ THE KINDS OF ROOM THIS HOTEL HAS (Sprint 49) ═══════════════════════════

          **THE CLIENT'S ANSWER, 17 SEPTEMBER:** every room in both hotels sleeps two or
          four. Until this sprint that fact lived inside an English adjective — "Twin" —
          which nothing could compute from and nobody could change.

          **IT IS A SHORT TABLE AND IT SITS ABOVE FIVE HUNDRED ROOMS** because it is read
          far more often than it is edited: a supervisor looking at a room's linen list
          wants to know what a "Room for four" is, and almost never wants to change it.

          **THERE IS NO "ADD".** A type arrives with the hotel's own room sheet. One
          invented here under a code the next upload does not use would quietly split the
          hotel's rooms across two types. */}
      <RoomTypesCard
        types={board.roomTypes}
        locale={locale}
        t={t}
        onEdit={(type) => setEditing({ kind: 'roomType', type })}
      />

      {said && <Said said={said} onClose={() => setSaid(null)} />}

      {editing.kind === 'room' && (
        <RoomForm
          propertyId={propertyId}
          room={editing.room}
          floors={board.floors}
          types={board.roomTypes}
          locale={locale}
          t={t}
          onCancel={() => setEditing({ kind: 'none' })}
          onSaved={done}
        />
      )}
      {editing.kind === 'inHotel' && (
        <InHotelForm
          propertyId={propertyId}
          room={editing.room}
          to={editing.to}
          locale={locale}
          t={t}
          onCancel={() => setEditing({ kind: 'none' })}
          onSaved={done}
        />
      )}
      {editing.kind === 'cleaning' && (
        <CleaningRulesForm
          propertyId={propertyId}
          rooms={editing.rooms}
          templates={board.templates}
          types={board.roomTypes}
          locale={locale}
          t={t}
          onCancel={() => setEditing({ kind: 'none' })}
          onSaved={done}
        />
      )}
      {editing.kind === 'roomType' && (
        <RoomTypeForm
          propertyId={propertyId}
          type={editing.type}
          locale={locale}
          t={t}
          onCancel={() => setEditing({ kind: 'none' })}
          onSaved={done}
        />
      )}
      {editing.kind === 'building' && (
        <BuildingForm
          propertyId={propertyId}
          locale={locale}
          t={t}
          onCancel={() => setEditing({ kind: 'none' })}
          onSaved={done}
        />
      )}
      {editing.kind === 'floor' && (
        <FloorForm
          propertyId={propertyId}
          buildings={board.buildings}
          locale={locale}
          t={t}
          onCancel={() => setEditing({ kind: 'none' })}
          onSaved={done}
        />
      )}
      {editing.kind === 'divisor' && (
        <DivisorForm
          propertyId={propertyId}
          current={board.minutesPerCredit}
          rooms={board.rooms}
          locale={locale}
          t={t}
          onCancel={() => setEditing({ kind: 'none' })}
          onSaved={done}
        />
      )}

      <Card>
        <CardHeader
          title={t.editListTitle.replace('{count}', formatCount(shown.length, locale))}
          icon={<Building2 className="size-4" />}
        />
        {shown.length === 0
          ? <div className="p-3.5">
              <EmptyState
                title={search.trim() === '' ? t.editEmpty : t.editNoMatch}
                body={search.trim() === '' ? '' : t.editSearch}
              />
            </div>
          : (
            <div className="max-h-[32rem] overflow-y-auto">
              <table className="w-full border-collapse text-[12.5px]">
                <thead className="sticky top-0 bg-card">
                  <tr className="border-b border-border text-[11px] text-muted-foreground">
                    <th className="p-2 text-start font-medium">{t.loadColRoom}</th>
                    <th className="p-2 text-start font-medium">{t.loadColFloor}</th>
                    <th className="p-2 text-start font-medium">{t.loadColType}</th>
                    {/* ── THE LINE DOWN THE MIDDLE OF THE TABLE ──────────────────
                        Pack 5 draws it and says what it is: rooms come from the
                        property system, how they are cleaned is ours. The border is
                        the pack's, and it is the most useful thing on this screen —
                        an administrator looking for what they are allowed to change
                        can see it without reading a word. */}
                    <th className="p-2 text-start font-medium border-s border-border">
                      {t.cleanColDeparture}
                    </th>
                    <th className="p-2 text-start font-medium">{t.cleanColStayover}</th>
                    {/* TWO COLUMNS FOR ONE FACT, AND ON PURPOSE THIS TIME — the second
                        is derived from the first in front of the reader. The minutes are
                        what the hotel STATES; the credits are what the assignment board
                        COUNTS. A supervisor who can see both, side by side, can work out
                        the divisor for themselves and stops wondering where 2.25 came
                        from. Only the minutes are editable. */}
                    <th className="p-2 text-end font-medium">{t.cleanColMinutes}</th>
                    <th className="p-2 text-end font-medium">{t.cleanColCredits}</th>
                    <th className="p-2 text-start font-medium">{t.cleanColDeepClean}</th>
                    <th className="p-2 text-start font-medium">{t.editColStatus}</th>
                    <th className="p-2 text-end font-medium" />
                  </tr>
                </thead>
                <tbody>
                  {shown.map((room) => (
                    <tr
                      key={room.roomId}
                      className={`border-b border-border last:border-0
                                  ${!room.inTheHotel
                                    ? 'bg-muted/40'
                                    : room.missingCleaningRules.length > 0
                                      /* AMBER, NOT RED. The pack's own colour, and the
                                         right one: nothing is broken and nobody has done
                                         anything wrong — a room arrived and nobody has
                                         said yet how it is cleaned. Red would read as a
                                         fault and send somebody looking for one. */
                                      ? 'bg-[#fffbeb] dark:bg-[#78350f]/25'
                                      : ''}`}
                    >
                      {/* AN IDENTIFIER: Western digits in both languages, because it is
                          painted on the door and read aloud. */}
                      <td className="p-2 font-mono font-semibold">{room.roomNumber}</td>
                      <td className="p-2">{room.floor}</td>
                      <td className="p-2">
                        <Bidi>{locale === 'ar' ? room.typeNameAr ?? room.typeName : room.typeName}</Bidi>
                        <span className="ms-1.5 text-[11px] text-muted-foreground">{room.typeCode}</span>
                      </td>
                      {/* HOW IT IS CLEANED. A room missing any of it cannot be given
                          to anybody, and the cell that is missing says so rather than
                          the row — "not configured" makes somebody read four columns. */}
                      <td className="p-2 border-s border-border">
                        <TemplateCell
                          templateId={room.departureTemplateId}
                          templates={board.templates}
                          missing={room.missingCleaningRules.includes('DEPARTURE_TEMPLATE')}
                          locale={locale}
                          t={t}
                        />
                      </td>
                      <td className="p-2">
                        <TemplateCell
                          templateId={room.stayoverTemplateId}
                          templates={board.templates}
                          missing={room.missingCleaningRules.includes('STAYOVER_TEMPLATE')}
                          locale={locale}
                          t={t}
                        />
                      </td>
                      {/* MINUTES AND CREDITS ARE BOTH QUANTITIES, so both localise —
                          unlike the room number three columns to the left, which is a
                          name. The minutes are a count and the credits go to one place;
                          see `numerals.ts` for why the two are formatted differently. */}
                      <td className="p-2 text-end tabular-nums">
                        {room.missingCleaningRules.includes('MINUTES')
                          ? <NotSet t={t} />
                          : formatCount(room.cleaningMinutes, locale)}
                      </td>
                      {/* GREY, BECAUSE IT IS NOT SOMETHING ANYBODY TYPED HERE. The
                          minutes beside it are black and editable; this is arithmetic the
                          server did, and drawing it in the same weight would invite
                          somebody to try to change it in the wrong place. */}
                      <td className="p-2 text-end tabular-nums text-muted-foreground">
                        {room.missingCleaningRules.includes('MINUTES')
                          ? <span aria-hidden>—</span>
                          : formatCredits(room.cleaningCredits, locale)}
                      </td>
                      <td className="p-2 text-[11px] text-muted-foreground">
                        <Bidi>{t.deepClean[room.deepClean]}</Bidi>
                      </td>
                      <td className="p-2">
                        {room.inTheHotel
                          ? (room.hasLiveCard && (
                              <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
                                <QrCode className="size-3" />
                                {t.editHasCard}
                              </span>
                            ))
                          : (
                            <span className="text-[11px] text-[#92400e] dark:text-[#fcd34d]">
                              <Bidi>
                                {room.removedReason
                                  ? t.editRemovedBecause
                                      .replace('{date}', localDate(room.removedAtUtc, locale))
                                      .replace('{reason}', room.removedReason)
                                  : t.editOutOfHotel}
                              </Bidi>
                            </span>
                          )}
                      </td>
                      <td className="p-2 text-end whitespace-nowrap">
                        {room.inTheHotel ? (
                          <>
                            <button
                              type="button"
                              onClick={() => setEditing({ kind: 'room', room })}
                              className="inline-flex items-center gap-1 rounded-hk border border-border
                                         px-2 py-1 text-[11px] hover:bg-muted cursor-pointer"
                            >
                              <Pencil className="size-3" />
                              {t.editEdit}
                            </button>
                            <button
                              type="button"
                              onClick={() => setEditing({ kind: 'cleaning', rooms: [room] })}
                              className="ms-1.5 inline-flex items-center gap-1 rounded-hk border border-border
                                         px-2 py-1 text-[11px] hover:bg-muted cursor-pointer"
                            >
                              <Sparkles className="size-3" />
                              {t.cleanEdit}
                            </button>
                            <button
                              type="button"
                              onClick={() => setEditing({ kind: 'inHotel', room, to: false })}
                              className="ms-1.5 inline-flex items-center gap-1 rounded-hk border border-border
                                         px-2 py-1 text-[11px] hover:bg-muted cursor-pointer"
                            >
                              <X className="size-3" />
                              {t.editRemove}
                            </button>
                          </>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setEditing({ kind: 'inHotel', room, to: true })}
                            className="inline-flex items-center gap-1 rounded-hk border border-border
                                       px-2 py-1 text-[11px] hover:bg-muted cursor-pointer"
                          >
                            <Undo2 className="size-3" />
                            {t.editRestore}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </Card>
    </div>
  )
}

/* ── what happened ────────────────────────────────────────────────────────── */

function Said({
  said, onClose,
}: { said: { text: string; warn?: string }; onClose: () => void }) {
  return (
    <div className="flex flex-col gap-2">
      <div
        role="status"
        className="flex items-start gap-2 rounded-hk border border-[#86efac] bg-[#f0fdf4]
                   px-3.5 py-3 text-[12.5px] text-[#166534]
                   dark:border-[#166534] dark:bg-[#0d2417] dark:text-[#86efac]"
      >
        <CheckCircle2 className="mt-0.5 size-4 shrink-0" />
        <span className="flex-1"><Bidi>{said.text}</Bidi></span>
        <button type="button" onClick={onClose} className="cursor-pointer" aria-label="close">
          <X className="size-3.5" />
        </button>
      </div>
      {/* THE REPRINT NOTICE IS A SEPARATE BLOCK IN A DIFFERENT COLOUR, because it is not
          part of "that worked" — it is a job somebody now has to do, on another screen,
          and folding it into the green line is how it gets read as decoration. */}
      {said.warn && (
        <div
          role="status"
          className="flex items-start gap-2 rounded-hk border border-[#fcd34d] bg-[#fffbeb]
                     px-3.5 py-3 text-[12.5px] text-[#92400e]
                     dark:border-[#92400e] dark:bg-[#2a1f07] dark:text-[#fcd34d]"
        >
          <TriangleAlert className="mt-0.5 size-4 shrink-0" />
          <span><Bidi>{said.warn}</Bidi></span>
        </div>
      )}
    </div>
  )
}

/* ── the room form ────────────────────────────────────────────────────────── */

function RoomForm({
  propertyId, room, floors, types, locale, t, onCancel, onSaved,
}: {
  propertyId: string
  room: RoomAdminRow | null
  floors: AdminFloor[]
  types: AdminRoomType[]
  locale: 'en' | 'ar'
  t: typeof STRINGS['en']
  onCancel: () => void
  onSaved: (text: string, warn?: string) => void
}) {
  const [roomNumber, setRoomNumber] = useState(room?.roomNumber ?? '')
  const [floorId, setFloorId] = useState(room?.floorId ?? floors[0]?.floorId ?? '')
  const [roomTypeId, setRoomTypeId] = useState(room?.roomTypeId ?? types[0]?.roomTypeId ?? '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)

  async function submit() {
    setBusy(true); setError(null)
    try {
      const saved = await saveRoom(propertyId, {
        ...(room ? { roomId: room.roomId } : {}),
        roomNumber, floorId, roomTypeId,
      })

      const number = saved.room.roomNumber
      const text =
        saved.action === null ? t.editNothingChanged
        : saved.action === 'CREATED' ? t.editCreated.replace('{roomNumber}', number)
        : saved.action === 'RENUMBERED' ? t.editRenumbered.replace('{roomNumber}', number)
        : t.editMoved.replace('{roomNumber}', number)

      /* TWO THINGS CAN BE WORTH SAYING AND ONLY ONE SLOT TO SAY THEM IN. The reprint
         wins: converting ٤٠٥ to 405 is visible in the row the person is looking at, and
         the card is not visible anywhere on this screen. */
      const warn =
        saved.cardNeedsReprinting ? t.editReprint.replace('{roomNumber}', number)
        : saved.convertedFromEasternDigits ? t.editConverted.replace('{roomNumber}', number)
        : undefined

      onSaved(text, warn)
    } catch (caught: unknown) {
      setError(caught)
    } finally {
      setBusy(false)
    }
  }

  return (
    <FormCard
      title={room ? `${t.editEdit} · ${room.roomNumber}` : t.editNewRoom}
      error={error}
      locale={locale}
      busy={busy}
      canSubmit={roomNumber.trim() !== '' && floorId !== '' && roomTypeId !== ''}
      t={t}
      onCancel={onCancel}
      onSubmit={submit}
    >
      <label className="flex flex-col gap-1 text-[12px]">
        <span className="font-medium">{t.editRoomNumber}</span>
        <input
          value={roomNumber}
          onChange={(e) => setRoomNumber(e.target.value)}
          aria-label={t.editRoomNumber}
          /* NOT type="number". A room number is an identifier, not a quantity: G12 and
             B1 are room numbers, and a numeric input would refuse them, strip a leading
             zero off 0101, and put spinner arrows on a door number. */
          className="h-9 w-40 rounded-hk border border-input bg-background px-2.5 font-mono text-[13px]"
        />
        <span className="text-[11px] text-muted-foreground">{t.editRoomNumberHint}</span>
        {room?.hasLiveCard === true && (
          <span className="inline-flex items-center gap-1 text-[11px] text-[#92400e] dark:text-[#fcd34d]">
            <QrCode className="size-3 shrink-0" />
            {t.editCardHint}
          </span>
        )}
      </label>

      <label className="flex flex-col gap-1 text-[12px]">
        <span className="font-medium">{t.editFloor}</span>
        <select
          value={floorId}
          onChange={(e) => setFloorId(e.target.value)}
          aria-label={t.editFloor}
          className="h-9 w-40 rounded-hk border border-input bg-background px-2.5 text-[13px]"
        >
          {floors.map((floor) => (
            <option key={floor.floorId} value={floor.floorId}>{floor.code}</option>
          ))}
        </select>
      </label>

      <label className="flex flex-col gap-1 text-[12px]">
        <span className="font-medium">{t.editType}</span>
        <select
          value={roomTypeId}
          onChange={(e) => setRoomTypeId(e.target.value)}
          aria-label={t.editType}
          className="h-9 min-w-48 rounded-hk border border-input bg-background px-2.5 text-[13px]"
        >
          {types.map((type) => (
            <option key={type.roomTypeId} value={type.roomTypeId}>
              {(locale === 'ar' ? type.nameAr ?? type.name : type.name)} · {type.code}
            </option>
          ))}
        </select>
      </label>
    </FormCard>
  )
}

/* ── in or out of the hotel ───────────────────────────────────────────────── */

function InHotelForm({
  propertyId, room, to, locale, t, onCancel, onSaved,
}: {
  propertyId: string
  room: RoomAdminRow
  to: boolean
  locale: 'en' | 'ar'
  t: typeof STRINGS['en']
  onCancel: () => void
  onSaved: (text: string, warn?: string) => void
}) {
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)

  async function submit() {
    setBusy(true); setError(null)
    try {
      const saved = await setRoomInHotel(propertyId, room.roomId, to, reason)
      onSaved((to ? t.editRestored : t.editRemoved)
        .replace('{roomNumber}', saved.roomNumber))
    } catch (caught: unknown) {
      setError(caught)
    } finally {
      setBusy(false)
    }
  }

  return (
    <FormCard
      title={`${to ? t.editRestore : t.editRemove} · ${room.roomNumber}`}
      error={error}
      locale={locale}
      busy={busy}
      /* THE BUTTON IS SHUT UNTIL THERE IS A SENTENCE IN THE BOX. The server refuses a
         blank reason too — this is not the enforcement, it is the courtesy of not
         letting somebody press a button that cannot work. */
      canSubmit={reason.trim() !== ''}
      t={t}
      onCancel={onCancel}
      onSubmit={submit}
    >
      <label className="flex min-w-0 flex-1 flex-col gap-1 text-[12px]">
        <span className="font-medium">{to ? t.editReasonRestore : t.editReasonRemove}</span>
        <input
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          aria-label={to ? t.editReasonRestore : t.editReasonRemove}
          className="h-9 w-full rounded-hk border border-input bg-background px-2.5 text-[13px]"
        />
        <span className="text-[11px] text-muted-foreground">
          {to ? t.editRestoreWarning : t.editReasonHint}
        </span>
      </label>
    </FormCard>
  )
}

/* ADDING A BUILDING, because the alternative is the anti-pattern this codebase has
   written down three times: a setting with nowhere to change it. The migration gives
   every existing hotel exactly one building called "Main building" — a hotel with a
   second tower and no way to say so would have to be given one by a developer.

   THERE IS DELIBERATELY NO WAY TO DELETE ONE. A building holds floors, floors hold
   rooms, and the foreign key is RESTRICT — so the honest screen is one that cannot offer
   an action the database will refuse. Renaming is the answer to a building that was
   created by mistake, and renaming is what W-127 already does to everything else. */
function BuildingForm({
  propertyId, locale, t, onCancel, onSaved,
}: {
  propertyId: string
  locale: 'en' | 'ar'
  t: typeof STRINGS['en']
  onCancel: () => void
  onSaved: (text: string) => void
}) {
  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const [nameAr, setNameAr] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)

  async function submit() {
    setBusy(true); setError(null)
    try {
      const building = await addBuilding(propertyId, {
        code,
        name,
        ...(nameAr.trim() === '' ? {} : { nameAr }),
      })
      onSaved(`${t.editAddBuilding} — ${building.name}`)
    } catch (caught: unknown) {
      setError(caught)
    } finally {
      setBusy(false)
    }
  }

  return (
    <FormCard
      title={t.editAddBuilding}
      error={error}
      locale={locale}
      busy={busy}
      /* BOTH, because a code is not a name. A building created with only "B" reads as
         "B" on every screen in the product and nobody ever goes back to fix it — so the
         server refuses it and the button does not pretend otherwise. */
      canSubmit={code.trim() !== '' && name.trim() !== ''}
      t={t}
      onCancel={onCancel}
      onSubmit={submit}
    >
      <label className="flex flex-col gap-1 text-[12px]">
        <span className="font-medium">{t.editBuildingCode}</span>
        <input
          id="building-code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          aria-label={t.editBuildingCode}
          className="h-9 w-32 rounded-hk border border-input bg-background px-2.5 font-mono text-[13px]"
        />
      </label>
      <label className="flex flex-col gap-1 text-[12px]">
        <span className="font-medium">{t.editBuildingName}</span>
        <input
          id="building-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          aria-label={t.editBuildingName}
          className="h-9 rounded-hk border border-input bg-background px-2.5 text-[13px]"
        />
      </label>
      <label className="flex flex-col gap-1 text-[12px]">
        <span className="font-medium">{t.editBuildingName} (AR)</span>
        <input
          id="building-name-ar"
          value={nameAr}
          onChange={(e) => setNameAr(e.target.value)}
          aria-label={`${t.editBuildingName} (AR)`}
          dir="rtl"
          className="h-9 rounded-hk border border-input bg-background px-2.5 text-[13px]"
        />
      </label>
    </FormCard>
  )
}

function FloorForm({
  propertyId, buildings, locale, t, onCancel, onSaved,
}: {
  propertyId: string
  buildings: readonly AdminBuilding[]
  locale: 'en' | 'ar'
  t: typeof STRINGS['en']
  onCancel: () => void
  onSaved: (text: string) => void
}) {
  const [code, setCode] = useState('')
  /* THE FIRST BUILDING, NOT AN EMPTY CHOICE. A hotel of one building — which is most of
     them, and every hotel on the morning after the migration — would otherwise have to
     answer a question with one possible answer before it could add a floor. The select
     is still drawn, because a hotel of four towers must not have the building chosen
     for it silently. */
  const [buildingId, setBuildingId] = useState(buildings[0]?.buildingId ?? '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)

  async function submit() {
    setBusy(true); setError(null)
    try {
      const floor = await addFloor(propertyId, buildingId, code)
      onSaved(`${t.editFloorCode} ${floor.code}`)
    } catch (caught: unknown) {
      setError(caught)
    } finally {
      setBusy(false)
    }
  }

  return (
    <FormCard
      title={t.editAddFloor}
      error={error}
      locale={locale}
      busy={busy}
      canSubmit={code.trim() !== '' && buildingId !== ''}
      t={t}
      onCancel={onCancel}
      onSubmit={submit}
    >
      {/* THE PLATFORM'S OWN `select`, not the design system's. A Radix `Select` nested in
          a dialog hangs jsdom for the better part of a minute on open, whatever the option
          count — found in Sprint 33 and the reason every select on this screen is native.
          It is also the right control on a handset. */}
      <label className="flex flex-col gap-1 text-[12px]">
        <span className="font-medium">{t.building}</span>
        <select
          id="floor-building"
          value={buildingId}
          onChange={(e) => setBuildingId(e.target.value)}
          aria-label={t.building}
          className="h-9 rounded-hk border border-input bg-background px-2.5 text-[13px]"
        >
          {buildings.map((b) => (
            <option key={b.buildingId} value={b.buildingId}>
              {nameIn(locale, b.name, b.nameAr)}
            </option>
          ))}
        </select>
      </label>
      <label className="flex flex-col gap-1 text-[12px]">
        <span className="font-medium">{t.editFloorCode}</span>
        <input
          value={code}
          onChange={(e) => setCode(e.target.value)}
          aria-label={t.editFloorCode}
          className="h-9 w-32 rounded-hk border border-input bg-background px-2.5 font-mono text-[13px]"
        />
      </label>
    </FormCard>
  )
}

/* ── the frame all three forms sit in ─────────────────────────────────────── */

function FormCard({
  title, error, locale, busy, canSubmit, t, onCancel, onSubmit, children,
}: {
  title: string
  error: unknown
  locale: 'en' | 'ar'
  busy: boolean
  canSubmit: boolean
  t: typeof STRINGS['en']
  onCancel: () => void
  onSubmit: () => void
  children: React.ReactNode
}) {
  return (
    <Card>
      <CardHeader title={title} icon={<Pencil className="size-4" />} />
      <form
        onSubmit={(e) => { e.preventDefault(); if (canSubmit && !busy) void onSubmit() }}
        className="flex flex-col gap-3 p-3.5"
      >
        <div className="flex flex-wrap items-start gap-3">{children}</div>

        {/* THE REFUSAL SITS INSIDE THE FORM, above the buttons, where the person is
            looking — not at the top of the screen where a refusal about a room number
            would be read after they have already scrolled away from the box. */}
        {error !== null && (
          <div
            role="alert"
            className="flex items-start gap-2 rounded-hk border border-[#fca5a5] bg-[#fef2f2]
                       px-3 py-2 text-[12.5px] text-[#991b1b]
                       dark:border-[#7f1d1d] dark:bg-[#2a0f0f] dark:text-[#fca5a5]"
          >
            <TriangleAlert className="mt-0.5 size-4 shrink-0" />
            <span><Bidi>{translateError(error, locale).message}</Bidi></span>
          </div>
        )}

        <div className="flex items-center gap-2">
          <button
            type="submit"
            disabled={!canSubmit || busy}
            className="rounded-hk bg-[var(--hk-primary)] px-3.5 py-2 text-xs font-medium text-white
                       disabled:bg-muted disabled:text-muted-foreground disabled:cursor-not-allowed
                       cursor-pointer"
          >
            {busy ? t.editSaving : t.editSave}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="rounded-hk border border-border px-3.5 py-2 text-xs font-medium
                       hover:bg-muted cursor-pointer"
          >
            {t.editCancel}
          </button>
        </div>
      </form>
    </Card>
  )
}

/**
 * **WHAT ONE CREDIT IS WORTH, FOR THE WHOLE PROPERTY.**
 *
 * ITS OWN FORM, and the only one on this screen that is not about a room. Setting a room's
 * minutes changes one room; this changes what every lane in the hotel reads tomorrow
 * morning without any room's minutes moving at all. Two acts at two scales, and a save
 * that could do both is a save where somebody adjusts a suite and re-balances the
 * building.
 *
 * IT SHOWS THE CONSEQUENCE BEFORE THE SAVE. A divisor on its own is a number with no
 * meaning — nobody knows what 15 does. So the form prices a real room from the table at
 * the typed value, live, and a supervisor decides by reading a sentence about a room they
 * know rather than by doing division in their head.
 */
function DivisorForm({
  propertyId, current, rooms, locale, t, onCancel, onSaved,
}: {
  propertyId: string
  current: number
  rooms: RoomAdminRow[]
  locale: 'en' | 'ar'
  t: typeof STRINGS['en']
  onCancel: () => void
  onSaved: (text: string) => void
}) {
  const [minutes, setMinutes] = useState<number | null>(current)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)

  const typed = minutes ?? 0
  /* THE SAME RANGE THE SERVER REFUSES, and it is here as well ON PURPOSE — which is not
     the "second opinion" mistake this project keeps recording. The server's refusal is the
     RULE and it is the one that decides; this only decides whether to draw a preview, and
     a preview of a division by zero is worse than no preview. When the two disagree the
     server still wins: the button stays enabled and the refusal arrives in words. */
  const previewable = Number.isInteger(typed) && typed >= 5 && typed <= 120

  /* A ROOM THE READER RECOGNISES, not a worked example. The longest clean in the hotel,
     because that is the row where a change to the divisor shows most — and its number is
     on a door, so the sentence is about something real. */
  const worst = useMemo(
    () => rooms
      .filter((r) => r.inTheHotel && r.cleaningMinutes > 0)
      .sort((a, b) => b.cleaningMinutes - a.cleaningMinutes)[0] ?? null,
    [rooms])

  const submit = async () => {
    setBusy(true)
    setError(null)
    try {
      await setMinutesPerCredit(propertyId, typed)
      onSaved(t.divisorSaved.replace('{minutes}', formatCount(typed, locale)))
    } catch (caught) {
      setError(caught)
    } finally {
      setBusy(false)
    }
  }

  return (
    <FormCard
      title={t.divisorTitle}
      error={error}
      locale={locale}
      busy={busy}
      canSubmit={minutes !== null && typed !== current}
      t={t}
      onCancel={onCancel}
      onSubmit={submit}
    >
      <div className="flex w-full flex-col gap-3">
        <p className="max-w-[68ch] text-[12.5px] leading-relaxed text-muted-foreground">
          {t.divisorIntro}
        </p>

        <Field label={t.divisorLabel} hint={t.divisorHint}>
          <NumberField
            value={minutes}
            onChange={setMinutes}
            aria-label={t.divisorLabel}
            className="h-8 w-28 px-2.5 text-[12.5px]"
          />
        </Field>

        {/* THE SENTENCE THAT MAKES THE NUMBER MEAN SOMETHING. Drawn as a status region so
            a screen reader hears it change as the field is typed in — the preview is the
            information here, and a sighted user watching it update is getting something a
            blind user would otherwise not.

            **AND YES, THIS DIVIDES ON THE CLIENT**, which is the thing `roomAdmin.ts` says
            not to do about the credits in the table. The difference is that those credits
            are a SAVED FACT and this is a PREVIEW OF A VALUE THAT DOES NOT EXIST YET: there
            is nothing on the server to ask, because the divisor being previewed is a number
            somebody is still typing. What makes it safe is that it is never stored and
            never re-read — the moment the save returns, every figure on the screen comes
            from the board the server sent back, this sentence included.

            The rounding is deliberately the server's: two places, halves away from zero.
            `Math.round(x * 100) / 100` is half-up, which is the same thing for positive
            numbers, and minutes and divisors are both always positive. If the preview and
            the saved row ever showed different second decimals, a supervisor would trust
            neither. */}
        {worst !== null && previewable && (
          <p
            role="status"
            className="max-w-[68ch] rounded-hk border border-border bg-muted/40 p-2.5
                       text-[12.5px] leading-relaxed"
          >
            <Bidi>
              {t.divisorPreview
                .replace('{room}', worst.roomNumber)
                .replace('{minutes}', formatCount(worst.cleaningMinutes, locale))
                .replace('{was}', formatCredits(worst.cleaningCredits, locale))
                .replace('{now}', formatCredits(
                  Math.round((worst.cleaningMinutes / typed) * 100) / 100, locale))}
            </Bidi>
          </p>
        )}
      </div>
    </FormCard>
  )
}

/**
 * The date a room left the hotel, in the reader's own calendar.
 *
 * `ar` defaults to the Hijri calendar in `Intl`, which is right here — this is a date
 * somebody reads, not a date on the wire. The wire keeps ISO and Gregorian, always.
 */
function localDate(iso: string | null, locale: 'en' | 'ar'): string {
  if (iso === null) return ''
  return new Intl.DateTimeFormat(locale === 'ar' ? 'ar-IQ' : 'en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
  }).format(new Date(iso))
}

/* ════════════════════════════════════════════════════════════════════════════
   W-127'S OTHER HALF — HOW A ROOM IS CLEANED.

   The pack calls this "the boundary the whole product runs on", and it is not a
   figure of speech: a room with no cleaning rules is one no supervisor can give to
   anybody, because the server refuses the assignment. Everything here exists to make
   that refusal something an administrator can see coming and fix in one action.
   ═════════════════════════════════════════════════════════════════════════ */

/** "Departure clean v7", or an amber "Not set". */
function TemplateCell({
  templateId, templates, missing, locale, t,
}: {
  templateId: string | null
  templates: AdminChecklistTemplate[]
  missing: boolean
  locale: 'en' | 'ar'
  t: (typeof STRINGS)['en']
}) {
  if (missing || templateId === null) return <NotSet t={t} />

  const template = templates.find((x) => x.templateId === templateId)
  // A template the board does not list — deactivated since the room was pointed at it.
  // Drawn as the id nobody can read? No: said plainly, because the room IS misconfigured
  // and the honest sentence is the one that sends somebody to the checklist screen.
  if (!template) return <span className="text-[11px] text-muted-foreground">{t.cleanUnknownTemplate}</span>

  return (
    <span className="inline-flex items-baseline gap-1.5">
      {/* THE NAME IN THE READER'S LANGUAGE. It was `template.name` — the English one,
          drawn down the middle of an Arabic table, because `ChecklistTemplate` had no
          Arabic column at all until Sprint 30. Falls back to the English rather than a
          blank: untranslated should read as untranslated. */}
      <Bidi>{templateName(template, locale)}</Bidi>
      {/* A VERSION NUMBER IS A NAME, not a quantity — never localised, and never
          `type="number"`. It is also NOT what the room stores: the room names the
          template, and this is whichever version is live today. */}
      {template.liveVersionNumber !== null
        ? <span className="text-[11px] text-muted-foreground">v{template.liveVersionNumber}</span>
        : <span className="text-[11px] text-[#92400e] dark:text-[#fcd34d]">{t.cleanNeverPublished}</span>}
    </span>
  )
}

function NotSet({ t }: { t: (typeof STRINGS)['en'] }) {
  return (
    <span className="text-[11px] font-medium text-[#92400e] dark:text-[#fcd34d]">
      {t.cleanNotSet}
    </span>
  )
}

/**
 * SETTING HOW ONE ROOM — OR FOUR HUNDRED — ARE CLEANED.
 *
 * ONE FORM FOR BOTH, because they are one write. The pack draws "Apply the Twin
 * defaults" as a button on a single row; making it a bulk form over the rooms that are
 * missing something is the same action with the scale it is actually used at, and it
 * avoids a second code path that would eventually validate differently.
 *
 * WHAT IT REPORTS BACK IS HOW MANY ROOMS CAN NOW BE GIVEN TO SOMEBODY. Not "saved" —
 * the administrator did not come here to save, they came to stop rooms being stuck, and
 * that is the number they wanted. A bulk apply over forty rooms that were already fine
 * reports 0, which is the truth and is worth knowing.
 */
function CleaningRulesForm({
  propertyId, rooms, templates, types, locale, t, onCancel, onSaved,
}: {
  propertyId: string
  rooms: RoomAdminRow[]
  templates: AdminChecklistTemplate[]
  types: AdminRoomType[]
  locale: 'en' | 'ar'
  t: (typeof STRINGS)['en']
  onCancel: () => void
  onSaved: (said: string) => void
}) {
  const one = rooms.length === 1 ? rooms[0] : null

  /* SEEDED FROM THE ROOM ONLY WHEN THERE IS ONE.
     Over a set of rooms there is no "current value" to show — forty rooms may hold
     forty different answers — so the fields start empty and an empty field means LEAVE
     IT ALONE. A bulk form pre-filled from the first room is how somebody overwrites
     thirty-nine rooms they never looked at. */
  const [departure, setDeparture] = useState(one?.departureTemplateId ?? '')
  const [stayover, setStayover] = useState(one?.stayoverTemplateId ?? '')
  const [minutes, setMinutes] = useState<number | null>(
    one && one.cleaningMinutes > 0 ? one.cleaningMinutes : null)
  const [deepClean, setDeepClean] = useState<DeepCleanFrequency | ''>(one?.deepClean ?? '')
  const [notes, setNotes] = useState(one?.cleaningNotes ?? '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)

  /* THE TYPE'S DEFAULTS, when every room in the set is the same type.
     Offering "apply the Twin defaults" over a mixed selection would mean applying one
     type's answers to another type's rooms, which is the mistake the button is named
     after not making. */
  const onlyType = useMemo(() => {
    const ids = new Set(rooms.map((r) => r.roomTypeId))
    return ids.size === 1 ? types.find((x) => x.roomTypeId === [...ids][0]) ?? null : null
  }, [rooms, types])

  const defaultsAreSet = onlyType !== null
    && onlyType.defaultDepartureTemplateId !== null
    && onlyType.defaultStayoverTemplateId !== null
    && onlyType.cleanMinutes > 0

  const applyDefaults = () => {
    if (!onlyType) return
    /* FILLS, DOES NOT OVERWRITE — the same rule as the server's `FillFrom`, and for the
       same reason: somebody who deliberately gave this room a different list must not
       have that reverted by a button whose name says "apply the defaults". */
    setDeparture((v) => v || onlyType.defaultDepartureTemplateId || '')
    setStayover((v) => v || onlyType.defaultStayoverTemplateId || '')
    setMinutes((v) => v ?? (onlyType.cleanMinutes > 0 ? onlyType.cleanMinutes : null))
    setDeepClean((v) => v || onlyType.defaultDeepClean)
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const saved = await setCleaningRules(propertyId, {
        roomIds: rooms.map((r) => r.roomId),
        // Only what was filled in. Undefined is "leave it alone" all the way to the
        // server, which is what makes this form usable over a set.
        ...(departure ? { departureTemplateId: departure } : {}),
        ...(stayover ? { stayoverTemplateId: stayover } : {}),
        ...(minutes !== null ? { minutes } : {}),
        ...(deepClean ? { deepClean } : {}),
        ...(one ? { notes } : {}),
      })
      onSaved(
        saved.nowAssignable > 0
          ? t.cleanNowAssignable.replace('{count}', formatCount(saved.nowAssignable, locale))
          : t.cleanSaved)
    } catch (caught) {
      setError(caught)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card>
      <CardHeader
        title={one
          ? t.cleanTitleOne.replace('{room}', one.roomNumber)
          : t.cleanTitleMany.replace('{count}', formatCount(rooms.length, locale))}
        icon={<Sparkles className="size-4" />}
      />
      <form onSubmit={submit} className="flex flex-col gap-3 p-3.5">
        <p className="max-w-[68ch] text-[12.5px] leading-relaxed text-muted-foreground">
          {one ? t.cleanIntro : t.cleanIntroMany}
        </p>

        {onlyType && (
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={applyDefaults}
              disabled={!defaultsAreSet}
              className="inline-flex items-center gap-1.5 rounded-hk border border-border
                         px-2.5 py-1 text-xs font-medium hover:bg-muted cursor-pointer
                         disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Sparkles className="size-3.5" />
              <Bidi>
                {t.cleanApplyDefaults.replace(
                  '{type}', locale === 'ar' ? onlyType.nameAr ?? onlyType.name : onlyType.name)}
              </Bidi>
            </button>
            {/* A DISABLED CONTROL MUST SAY WHICH REASON IT IS. Here there is only one —
                the type has no defaults yet — and saying it is what stops somebody
                pressing a dead button twice and concluding the screen is broken. */}
            {!defaultsAreSet && (
              <span className="text-[11px] text-muted-foreground">{t.cleanNoDefaults}</span>
            )}
          </div>
        )}

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={t.cleanColDeparture}>
            <TemplateChoice
              value={departure} onChange={setDeparture} templates={templates}
              locale={locale} t={t} />
          </Field>
          <Field label={t.cleanColStayover}>
            <TemplateChoice
              value={stayover} onChange={setStayover} templates={templates}
              locale={locale} t={t} />
          </Field>
          <Field label={t.cleanColMinutes} hint={t.cleanMinutesHint}>
            {/* NOT `type="number"`. A spinner in an Arabic interface is the control that
                produces a figure typed in Arabic-Indic digits and then silently
                discarded. `inputMode` gets the right keypad without it.

                `numeric`, NOT `decimal`, AND THAT IS THE CHANGE SPRINT 32 MADE HERE: the
                field used to hold credits, which are 1.5 and 2.25, and now it holds
                minutes, which are whole. A decimal keypad on a whole number invites "30.5
                minutes", which the server would round and the hotel would not have meant. */}
            {/* **AND CQ-139 FINISHES WHAT THIS COMMENT STARTED.** The field avoided the
                spinner and then parsed with `Number()`, which turns ٣٠ into NaN — so the
                Arabic figure was accepted by the keyboard and discarded one line later,
                which is the same defect with an extra step. */}
            <NumberField
              value={minutes}
              onChange={setMinutes}
              placeholder={one ? '' : t.cleanLeaveAlone}
              aria-label={t.cleanColMinutes}
              className="h-8 w-full px-2.5 text-[12.5px]"
            />
          </Field>
          <Field label={t.cleanColDeepClean}>
            <select
              value={deepClean}
              onChange={(e) => setDeepClean(e.target.value as DeepCleanFrequency | '')}
              aria-label={t.cleanColDeepClean}
              className="h-8 w-full rounded-hk border border-input bg-background px-2 text-[12.5px]"
            >
              <option value="">{one ? t.cleanChoose : t.cleanLeaveAlone}</option>
              {(['NONE', 'MONTHLY', 'QUARTERLY', 'TWICE_YEARLY', 'YEARLY'] as const).map((f) => (
                <option key={f} value={f}>{t.deepClean[f]}</option>
              ))}
            </select>
          </Field>
        </div>

        {/* NOTES ARE PER ROOM AND ONLY WHEN THERE IS ONE. "The balcony door sticks" is
            true of a room, and writing it across forty at once is how a note stops
            meaning anything. */}
        {one && (
          <Field label={t.cleanNotes}>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              /* AN ARABIC TEXT BOX IS `dir="auto"` WHATEVER LANGUAGE THE SCREEN IS IN —
                 a note typed in Arabic on an English screen must not render backwards. */
              dir="auto"
              aria-label={t.cleanNotes}
              className="w-full rounded-hk border border-input bg-background p-2 text-[12.5px]"
            />
          </Field>
        )}

        {error !== null && (
          <p className="text-[12.5px] text-[var(--hk-danger)]">
            <Bidi>{translateError(error, locale).message}</Bidi>
          </p>
        )}

        <div className="flex gap-2">
          <button
            type="submit"
            disabled={busy}
            className="rounded-hk bg-[var(--hk-primary)] px-3.5 py-2 text-xs font-medium
                       text-white disabled:opacity-60 cursor-pointer"
          >
            {busy ? t.editSaving : t.editSave}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="rounded-hk border border-border px-3.5 py-2 text-xs font-medium
                       hover:bg-muted cursor-pointer"
          >
            {t.editCancel}
          </button>
        </div>
      </form>
    </Card>
  )
}

function TemplateChoice({
  value, onChange, templates, locale, t,
}: {
  value: string
  onChange: (v: string) => void
  templates: AdminChecklistTemplate[]
  locale: 'en' | 'ar'
  t: (typeof STRINGS)['en']
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="h-8 w-full rounded-hk border border-input bg-background px-2 text-[12.5px]"
    >
      <option value="">{t.cleanChoose}</option>
      {templates.map((x) => (
        <option key={x.templateId} value={x.templateId}>
          {x.liveVersionNumber !== null
            ? `${templateName(x, locale)} v${x.liveVersionNumber}`
            /* SAID, NOT HIDDEN. A template with nothing published is choosable and gives
               the room no list at all — an administrator choosing it should be told that
               here rather than finding out when an attendant opens an empty checklist. */
            : `${templateName(x, locale)} — ${t.cleanNeverPublished}`}
        </option>
      ))}
    </select>
  )
}

/** A labelled control, with an optional line UNDER the label rather than beside it — a
 *  hint set after the field is a hint read after the mistake. */
function Field({ label, hint, children }: {
  label: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <label className="flex flex-col gap-1 text-[12.5px]">
      <span className="font-medium">{label}</span>
      {hint && <span className="text-[11px] font-normal text-muted-foreground">{hint}</span>}
      {children}
    </label>
  )
}

/* ══ THE KINDS OF ROOM, AND EDITING ONE (Sprint 49) ═════════════════════════════

   The client's answer on 17 September: *"in all location it is 1 room with 4 person or
   1 room with 2 person"*. Both hotels, every building.
   ═══════════════════════════════════════════════════════════════════════════ */

function RoomTypesCard({
  types, locale, t, onEdit,
}: {
  types: readonly AdminRoomType[]
  locale: 'en' | 'ar'
  t: typeof STRINGS['en']
  onEdit: (type: AdminRoomType) => void
}) {
  if (types.length === 0) return null

  return (
    <Card>
      <CardHeader title={t.typesTitle} icon={<Building2 className="size-4" />} />
      <table className="w-full border-collapse text-[12.5px]">
        <thead>
          <tr className="border-b border-border text-[11px] text-muted-foreground">
            <th className="p-2 text-start font-medium">{t.typesName}</th>
            <th className="p-2 text-start font-medium">{t.typesSleeps}</th>
            <th className="p-2 text-start font-medium">{t.typesMinutes}</th>
            <th className="p-2 text-start font-medium">{t.typesRooms}</th>
            <th className="p-2" />
          </tr>
        </thead>
        <tbody>
          {types.map((type) => (
            <tr key={type.roomTypeId} className="border-b border-border last:border-0">
              <td className="p-2">
                <Bidi>{nameIn(locale, type.name, type.nameAr)}</Bidi>
                {/* THE CODE, ALWAYS VISIBLE AND NEVER TRANSLATED. It is what every report
                    counts by and what the next upload matches on, so it is the thing an
                    administrator needs in front of them when they edit a sheet. */}
                <span className="ms-1.5 font-mono text-[11px] text-muted-foreground">
                  {type.code}
                </span>
              </td>
              <td className="p-2 tabular-nums">{formatCount(type.capacity, locale)}</td>
              <td className="p-2 tabular-nums text-muted-foreground">
                {formatCount(type.cleanMinutes, locale)}
              </td>
              <td className="p-2 tabular-nums text-muted-foreground">
                {formatCount(type.rooms, locale)}
              </td>
              <td className="p-2 text-end">
                {/* **"EDIT" TWICE ON ONE SCREEN IS "EDIT" NOWHERE.** The rooms table below
                    has an Edit on every row, and a person using a screen reader would hear
                    the same single word for both — so this one names what it edits. Found
                    the moment the card landed: two tests that asked for the button called
                    "Edit" stopped being able to say which. */}
                <button
                  type="button"
                  onClick={() => onEdit(type)}
                  aria-label={`${t.editEdit} — ${nameIn(locale, type.name, type.nameAr)}`}
                  className="inline-flex items-center gap-1.5 rounded-hk border border-border
                             px-2 py-1 text-[11px] hover:bg-muted cursor-pointer"
                >
                  <Pencil className="size-3" />
                  {t.editEdit}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  )
}

/**
 * **EDITING A KIND OF ROOM.**
 *
 * **THE CODE IS SHOWN AND CANNOT BE CHANGED.** It is what the hotel's spreadsheet says
 * and what every report counts by; renaming it here would make the next upload match
 * nothing, create a second type and split four hundred rooms between them — silently,
 * because both would look perfectly valid.
 *
 * **NOTHING HERE REACHES INTO A ROOM.** The minutes are the type's DEFAULT, copied onto a
 * room when it is set up; a save that re-priced four hundred rooms mid-shift, with tasks
 * already raised against the old numbers, is the thing Sprint 29 decided against and this
 * form keeps to.
 */
function RoomTypeForm({
  propertyId, type, locale, t, onCancel, onSaved,
}: {
  propertyId: string
  type: AdminRoomType
  locale: 'en' | 'ar'
  t: typeof STRINGS['en']
  onCancel: () => void
  onSaved: (text: string) => void
}) {
  const [name, setName] = useState(type.name)
  const [nameAr, setNameAr] = useState(type.nameAr ?? '')
  const [capacity, setCapacity] = useState<number | null>(type.capacity)
  const [minutes, setMinutes] = useState<number | null>(type.cleanMinutes)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)

  /* **NULL IS AN EMPTY BOX, NOT A ZERO** — see `NumberField`. A cleared field must not
     read as "sleeps nobody" and quietly become a valid save. */
  const people = capacity
  const mins = minutes
  const usable =
    name.trim() !== ''
    && people !== null && Number.isInteger(people) && people >= 1 && people <= 12
    && mins !== null && Number.isInteger(mins) && mins > 0 && mins <= 600

  async function submit() {
    setBusy(true); setError(null)
    try {
      const saved = await saveRoomType(propertyId, type.roomTypeId, {
        name,
        nameAr: nameAr.trim() === '' ? null : nameAr,
        capacity: people!,
        cleanMinutes: mins!,
      })
      onSaved(`${saved.name} — ${t.typesSleeps} ${formatCount(saved.capacity, locale)}`)
    } catch (caught: unknown) {
      setError(caught)
    } finally {
      setBusy(false)
    }
  }

  return (
    <FormCard
      title={`${t.typesEditTitle} — ${type.code}`}
      error={error}
      locale={locale}
      busy={busy}
      canSubmit={usable}
      t={t}
      onCancel={onCancel}
      onSubmit={submit}
    >
      <label className="flex flex-col gap-1 text-[12px]">
        <span className="font-medium">{t.typesName}</span>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          aria-label={t.typesName}
          className="h-9 rounded-hk border border-input bg-background px-2.5 text-[13px]"
        />
      </label>
      <label className="flex flex-col gap-1 text-[12px]">
        <span className="font-medium">{t.typesName} (AR)</span>
        <input
          value={nameAr}
          onChange={(e) => setNameAr(e.target.value)}
          aria-label={`${t.typesName} (AR)`}
          dir="rtl"
          className="h-9 rounded-hk border border-input bg-background px-2.5 text-[13px]"
        />
      </label>
      <label className="flex flex-col gap-1 text-[12px]">
        <span className="font-medium">{t.typesSleeps}</span>
        {/* **CQ-139.** `type="number"` refuses ٤ at the keyboard, so on an Arabic screen
            full of ٤s this box could not be typed into. `NumberField` folds both sets. */}
        <NumberField
          min={1}
          max={12}
          value={capacity}
          onChange={setCapacity}
          aria-label={t.typesSleeps}
          className="h-9 w-24 px-2.5 text-[13px]"
        />
        {/* THE CONSEQUENCE, SAID WHERE THE NUMBER IS TYPED. This is not a label on a
            room — it is how much linen goes through the door of every room of this kind,
            every day, and the person changing it should be told so before they save. */}
        <span className="text-[11px] leading-snug text-muted-foreground">{t.typesSleepsNote}</span>
      </label>
      <label className="flex flex-col gap-1 text-[12px]">
        <span className="font-medium">{t.typesMinutes}</span>
        <NumberField
          min={1}
          max={600}
          value={minutes}
          onChange={setMinutes}
          aria-label={t.typesMinutes}
          className="h-9 w-24 px-2.5 text-[13px]"
        />
        {/* A DEFAULT, NOT A RE-PRICING. Said out loud because "change the minutes" reads
            like it should change every room, and it deliberately does not. */}
        <span className="text-[11px] leading-snug text-muted-foreground">{t.typesMinutesNote}</span>
      </label>
    </FormCard>
  )
}
